import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

const pad = (n) => n.toString().padStart(2, '0');

export function DateField({ name, value, onChange, placeholder, required, testId }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const today = new Date();
  const parseValue = () => {
    const m = (value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m) return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
    return null;
  };
  const selected = parseValue();
  const [viewDate, setViewDate] = useState(selected || today);

  const openPicker = () => {
    setViewDate(parseValue() || new Date());
    setOpen(!open);
  };

  const months = language === 'es'
    ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const weekdays = language === 'es' ? ['D', 'L', 'M', 'X', 'J', 'V', 'S'] : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const selectDay = (day) => {
    onChange({ target: { name, value: `${pad(month + 1)}/${pad(day)}/${year}` } });
    setOpen(false);
  };

  const isSelected = (day) => selected && selected.getDate() === day && selected.getMonth() === month && selected.getFullYear() === year;
  const isToday = (day) => today.getDate() === day && today.getMonth() === month && today.getFullYear() === year;

  return (
    <div className="relative" ref={ref}>
      <div className="flex">
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          className="w-full p-2 border rounded-l-md text-sm border-r-0"
        />
        <button
          type="button"
          onClick={openPicker}
          data-testid={`${testId}-picker-btn`}
          className="px-2.5 border rounded-r-md bg-gray-50 hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 transition-colors"
        >
          <CalendarIcon size={16} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border rounded-xl shadow-xl p-3 w-64" data-testid={`${testId}-picker`}>
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid={`${testId}-prev-month`}>
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold text-gray-800">{months[month]} {year}</span>
            <button type="button" onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid={`${testId}-next-month`}>
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-center">
            {weekdays.map((d, i) => (
              <span key={`wd-${i}-${d}`} className="text-[10px] font-bold text-gray-400 py-1">{d}</span>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => <span key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  data-testid={`${testId}-day-${day}`}
                  className={`text-xs py-1.5 rounded-lg transition-colors ${
                    isSelected(day) ? 'bg-emerald-600 text-white font-bold'
                    : isToday(day) ? 'bg-emerald-100 text-emerald-700 font-bold'
                    : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              onChange({ target: { name, value: `${pad(today.getMonth() + 1)}/${pad(today.getDate())}/${today.getFullYear()}` } });
              setOpen(false);
            }}
            className="w-full mt-2 text-xs font-bold text-emerald-600 hover:bg-emerald-50 py-1.5 rounded-lg"
            data-testid={`${testId}-today-btn`}
          >
            {language === 'es' ? 'Hoy' : 'Today'}
          </button>
        </div>
      )}
    </div>
  );
}

export function TimeField({ name, value, onChange, placeholder, required, testId }) {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutsideClose(ref, () => setOpen(false));

  const parts = (value || '').split(':');
  const sel = {
    h: parts[0] !== undefined && parts[0] !== '' ? Number(parts[0]) : null,
    m: parts[1] !== undefined && parts[1] !== '' ? Number(parts[1]) : null,
    s: parts[2] !== undefined && parts[2] !== '' ? Number(parts[2]) : null
  };

  const setPart = (part, val) => {
    const next = { ...sel, [part]: val };
    const v = `${pad(next.h ?? 0)}:${pad(next.m ?? 0)}:${pad(next.s ?? 0)}`;
    onChange({ target: { name, value: v } });
  };

  const setNow = () => {
    const now = new Date();
    onChange({ target: { name, value: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}` } });
  };

  const renderColumn = (label, part, max) => (
    <div className="flex-1 min-w-0">
      <p className="text-[10px] font-bold text-gray-400 text-center mb-1 uppercase">{label}</p>
      <div className="h-36 overflow-y-auto rounded-lg border bg-gray-50 scroll-smooth">
        {Array.from({ length: max }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPart(part, i)}
            data-testid={`${testId}-${part}-${i}`}
            className={`w-full text-xs py-1.5 transition-colors ${
              sel[part] === i ? 'bg-emerald-600 text-white font-bold' : 'hover:bg-emerald-50 text-gray-700'
            }`}
          >
            {pad(i)}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <div className="flex">
        <input
          type="text"
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          className="w-full p-2 border rounded-l-md text-sm border-r-0"
        />
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`${testId}-picker-btn`}
          className="px-2.5 border rounded-r-md bg-gray-50 hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 transition-colors"
        >
          <Clock size={16} />
        </button>
      </div>
      {open && (
        <div className="absolute z-50 mt-1 left-0 bg-white border rounded-xl shadow-xl p-3 w-64" data-testid={`${testId}-picker`}>
          <div className="flex gap-2">
            {renderColumn(language === 'es' ? 'Hora' : 'Hour', 'h', 24)}
            {renderColumn('Min', 'm', 60)}
            {renderColumn('Seg', 's', 60)}
          </div>
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={setNow}
              className="flex-1 text-xs font-bold text-emerald-600 hover:bg-emerald-50 py-1.5 rounded-lg border border-emerald-200"
              data-testid={`${testId}-now-btn`}
            >
              {language === 'es' ? 'Ahora' : 'Now'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="flex-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 py-1.5 rounded-lg"
              data-testid={`${testId}-ok-btn`}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
